import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { assignmentsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { STATUS_STYLES, STATUS_LABELS, errMsg } from '../lib/roles';

/** Every invitation and role the user holds on other people's events and series. */
export default function InvitationsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  const load = useCallback(() => {
    assignmentsAPI.mine().then(r => setItems(r.data)).catch(e => { toast.error(errMsg(e)); setItems([]); });
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/auth?redirect=/invitations'); return; }
    load();
  }, [authLoading, user, load, navigate]);

  const respond = async (a, accept) => {
    try {
      await (accept ? assignmentsAPI.accept(a.assignment_id) : assignmentsAPI.decline(a.assignment_id));
      toast.success(accept ? 'Accepted' : 'Declined');
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const leave = async (a) => {
    if (!window.confirm(`Leave your ${a.role_label} role on ${a.event?.title}?`)) return;
    try { await assignmentsAPI.remove(a.assignment_id); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!items) return <div className="min-h-screen bg-[#050505] pt-24 text-center text-gray-400">Loading…</div>;

  const pending = items.filter(a => a.status === 'pending');
  const active = items.filter(a => a.status === 'accepted');

  const Card = ({ a }) => {
    const isSeries = a.scope_type === 'series';
    const title = a.event?.title || 'Event';
    return (
      <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold">{title}{isSeries ? ' (series)' : ''}</p>
            <p className="text-gray-500 text-sm">
              {a.role_label}{a.event?.date && !isSeries ? ` · ${a.event.date}` : ''}{a.event?.city ? ` · ${a.event.city}` : ''}
            </p>
            {a.assigned_by && <p className="text-gray-500 text-sm">Invited by {a.assigned_by.name}</p>}
          </div>
          <span className={`text-[11px] font-bold border rounded-full px-2 py-0.5 ${STATUS_STYLES[a.status]}`}>{STATUS_LABELS[a.status]}</span>
        </div>
        {a.status === 'pending' ? (
          <div className="flex gap-2 mt-3">
            <button onClick={() => respond(a, false)} className="flex-1 border border-gray-700 text-gray-200 rounded py-2 text-sm font-bold hover:border-gray-500">Decline</button>
            <button onClick={() => respond(a, true)} className="flex-1 btn-primary rounded py-2 text-sm font-bold">Accept</button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-4 mt-3 text-sm">
            {!isSeries && <Link className="text-gray-300 hover:text-white" to={`/events/${a.scope_id}`}>Event page</Link>}
            <Link className="text-gray-300 hover:text-white" to={`/team/${a.scope_type}/${a.scope_id}`}>Team</Link>
            {a.permissions?.includes('checkin') && !isSeries && (
              <Link className="text-gray-300 hover:text-white" to={`/check-in/${a.scope_id}`}>Check-in</Link>
            )}
            {a.role === 'promoter' && <Link className="text-gray-300 hover:text-white" to="/promoter">Campaign</Link>}
            <button onClick={() => leave(a)} className="text-red-400 hover:text-red-300">Leave</button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-20 pb-16 page-enter">
      <div className="max-w-3xl mx-auto px-4 space-y-3">
        <h1 className="text-2xl md:text-3xl font-black text-white uppercase mb-4">Invitations & roles</h1>
        <p className="text-[11px] font-black tracking-widest text-gray-500">AWAITING YOUR ANSWER ({pending.length})</p>
        {pending.length === 0 && <p className="text-gray-500 text-sm">No pending invitations.</p>}
        {pending.map(a => <Card key={a.assignment_id} a={a} />)}
        <p className="text-[11px] font-black tracking-widest text-gray-500 pt-6">MY ROLES ({active.length})</p>
        {active.length === 0 && <p className="text-gray-500 text-sm">You aren't on any event teams yet.</p>}
        {active.map(a => <Card key={a.assignment_id} a={a} />)}
      </div>
    </div>
  );
}
